import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { IFilterModel } from 'src/app/shared/models/filter-model';
import { NotifyService } from 'src/app/shared/services/notify.service';
import { DownloadUtil } from 'src/app/shared/utils/download-util';
import { Filter } from 'src/app/shared/models/filter';
import { DateRange, ShippingAccountingFilterAdapter } from '../../data/adapters/shipping-accounting-filter.adapter';
import { SendShippingEmailRequest } from '../../domain/entities/shipping-accounting.entity';
import { DownloadShippingCsvUseCase } from '../../domain/use-cases/download-shipping-csv.use-case';
import { GetShippingAccountingUseCase } from '../../domain/use-cases/get-shipping-accounting.use-case';
import { SendShippingEmailUseCase } from '../../domain/use-cases/send-shipping-email.use-case';
import { ShippingAccountingStore } from '../store/shipping-accounting.store';
import { ShippingAccountingPageVm } from '../view-models/shipping-accounting-page.vm';

@Injectable()
export class ShippingAccountingFacade {
  private filter: Filter;
  private dateRange: DateRange;
  private currentPage = 1;

  readonly vm$: Observable<ShippingAccountingPageVm> = this.store.state$.pipe(
    map((state) => ({
      state: state.loading ? 'loading' : state.errorMessage ? 'error' : state.items.length === 0 ? 'empty' : 'success',
      items: state.items,
      total: state.total,
      hasMore: state.items.length < state.total,
      loadingMore: state.loadingMore,
      errorMessage: state.errorMessage,
      sendingEmail: state.sendingEmail,
      downloadingCsvFor: state.downloadingCsvFor,
      dateRange: state.dateRange,
    }))
  );

  constructor(
    private readonly store: ShippingAccountingStore,
    private readonly filterAdapter: ShippingAccountingFilterAdapter,
    private readonly getListUseCase: GetShippingAccountingUseCase,
    private readonly sendEmailUseCase: SendShippingEmailUseCase,
    private readonly downloadCsvUseCase: DownloadShippingCsvUseCase,
    private readonly notifyService: NotifyService
  ) {
    const init = filterAdapter.createDefaultFilter();
    this.filter = init.filter;
    this.dateRange = init.dateRange;
    this.store.setDateRange(this.dateRange);
  }

  load(): void {
    this.currentPage = 1;
    this.filter.page(1);
    this.store.setLoading();
    this.getListUseCase.execute(this.filter.convertFilterInQuery()).pipe(
      tap((list) => this.store.setItems(list.items, list.total)),
      catchError(() => {
        this.store.setError('Falha ao carregar contabilidade de envio');
        return of(null);
      })
    ).subscribe();
  }

  loadMore(): void {
    this.currentPage++;
    this.filter.page(this.currentPage);
    this.store.setLoadingMore();
    this.getListUseCase.execute(this.filter.convertFilterInQuery()).pipe(
      tap((list) => this.store.appendItems(list.items, list.total)),
      catchError(() => {
        this.store.setError('Falha ao carregar mais itens');
        return of(null);
      })
    ).subscribe();
  }

  filterBy(filters: readonly IFilterModel[]): void {
    filters.forEach((item) => {
      if (item.key === 'month' && item.filter.length > 0) {
        const raw = item.filter[0].value;
        let date: Date;
        if (raw instanceof Date) {
          date = raw;
        } else {
          const [year, month, day] = String(raw).split('-').map(Number);
          date = new Date(year, month - 1, day);
        }
        this.dateRange = this.filterAdapter.withMonthFilter(this.filter, date);
        this.store.setDateRange(this.dateRange);
      } else {
        this.filterAdapter.withFilterModels(this.filter, [item]);
      }
    });
    this.load();
  }

  sendEmail(request: SendShippingEmailRequest): void {
    this.store.setSendingEmail(true);
    this.sendEmailUseCase.execute(request).pipe(
      tap(() => {
        this.store.setSendingEmail(false);
        this.notifyService.emitSuccess('Enviado com sucesso');
        this.load();
      }),
      catchError(() => {
        this.store.setSendingEmail(false);
        this.store.setError('Falha ao enviar email');
        return of(null);
      })
    ).subscribe();
  }

  downloadCsv(professionalId: string): void {
    this.store.setDownloadingCsvFor(professionalId);
    this.downloadCsvUseCase.execute(professionalId, this.dateRange.start, this.dateRange.end).pipe(
      tap((blob) => {
        this.store.setDownloadingCsvFor(null);
        DownloadUtil.download(
          blob,
          `relatorio-${professionalId}-${this.dateRange.start}-${this.dateRange.end}.csv`,
          'text/csv'
        );
      }),
      catchError(() => {
        this.store.setDownloadingCsvFor(null);
        this.store.setError('Falha ao baixar relatório');
        return of(null);
      })
    ).subscribe();
  }
}
